import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { AppDialogProvider, useAppDialog } from "./AppDialogProvider";

import UncheckedParts from "./UncheckedParts";
import AllParts from "./AllParts";
import Gages from "./Gages";
import Print from "./Print";
import PIR from "./PIR";
import Unreleased from "./Unreleased";
import FrmJob from "./FrmJob";
import ImportFromNJ from "./ImportFromNJ";
import NewPart from "./NewPart";
import ConfirmPart from "./ConfirmPart";
import ReleasePart from "./ReleasePart";
import EditPart from "./EditPart";
import FrmSelect from "./FrmSelect";

const INITIAL_BUTTONS = {
  uncheckedParts: {
    label: "Unchecked Parts",
    subtitle: "Review parts waiting for validation",
    icon: "ri-checkbox-circle-line",
    tone: "primary",
    authorized: false,
  },
  allParts: {
    label: "All Parts",
    subtitle: "Browse and maintain the full catalog",
    icon: "ri-stack-line",
    tone: "info",
    authorized: false,
  },
  gages: {
    label: "Gages",
    subtitle: "Manage gage-related records",
    icon: "ri-ruler-2-line",
    tone: "success",
    authorized: false,
  },
  pir: {
    label: "PIR",
    subtitle: "Open PIR workflows and records",
    icon: "ri-file-list-3-line",
    tone: "warning",
    authorized: false,
  },
  release: {
    label: "Release",
    subtitle: "Release approved parts to the next step",
    icon: "ri-rocket-line",
    tone: "success",
    authorized: false,
  },
  print: {
    label: "Print",
    subtitle: "Generate outputs and print resources",
    icon: "ri-printer-line",
    tone: "dark",
    authorized: false,
  },
  simulate: {
    label: "Simulator",
    subtitle: "Legacy launch flow for simulation",
    icon: "ri-cpu-line",
    tone: "secondary",
    authorized: false,
  },
  job: {
    label: "Job",
    subtitle: "Work with job-specific operations",
    icon: "ri-briefcase-4-line",
    tone: "primary",
    authorized: false,
  },
  manual: {
    label: "Create New MAFIA",
    subtitle: "Create a new record manually",
    icon: "ri-add-circle-line",
    tone: "danger",
    authorized: false,
  },
  importNj: {
    label: "Import From NJ",
    subtitle: "Import external data into MAFIA Aux",
    icon: "ri-download-cloud-2-line",
    tone: "info",
    authorized: false,
  },
};

const MENU_ORDER = [
  "uncheckedParts",
  "allParts",
  "gages",
  "pir",
  "release",
  "print",
  "job",
  "manual",
  "importNj",
  "simulate",
];

function MenuPageContent() {
  const dialog = useAppDialog();

  const [countryCode, setCountryCode] = useState("");
  const [permissionLevel, setPermissionLevel] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState("menu");
  const [pageProps, setPageProps] = useState({});

  useEffect(() => {
    initializePage();
  }, []);

  async function initializePage() {
    try {
      const country = await getCountryCode();
      const permission = await getMyPermissionLevel();
      setCountryCode(country);
      setPermissionLevel(permission);
    } catch (err) {
      console.error(err);
      await dialog.error("Failed to initialize MAFIA Aux.");
    } finally {
      setLoading(false);
    }
  }

  const computedButtons = useMemo(() => {
    const buttons = JSON.parse(JSON.stringify(INITIAL_BUTTONS));

    Object.keys(buttons).forEach((key) => {
      buttons[key].authorized = true;
    });

    if (permissionLevel < 1) {
      buttons.release.authorized = false;
      buttons.manual.authorized = false;
      buttons.importNj.authorized = false;
    }

    if (permissionLevel < 2) {
      buttons.simulate.authorized = false;
    }

    if (countryCode !== "INDIA") {
      buttons.importNj.label = "Import From NJ / India";
    }

    return buttons;
  }, [countryCode, permissionLevel]);

  const environmentText = useMemo(() => {
    if (countryCode === "USA") return "USA Database";
    if (countryCode === "INDIA") return "India Database";
    return "Unknown Database";
  }, [countryCode]);

  const accessibleCount = useMemo(
    () => Object.values(computedButtons).filter((item) => item.authorized).length,
    [computedButtons]
  );

  const visibleButtons = useMemo(() => {
    return MENU_ORDER.filter((key) => computedButtons[key]).map((key) => ({
      key,
      ...computedButtons[key],
    }));
  }, [computedButtons]);

  function openPage(pageKey, props = {}) {
    setPageProps(props);
    setActivePage(pageKey);
  }

  function goBackToMenu() {
    setPageProps({});
    setActivePage("menu");
  }

  async function handleLaunchSimulator() {
    try {
      const result = await launchSimulator({ countryCode });

      if (!result?.success) {
        await dialog.warning(
          result?.message ||
            "Failed to launch simulator. Please try again or contact technical support."
        );
      }
    } catch (error) {
      console.error("Failed to launch simulator:", error);
      await dialog.error(
        "Failed to launch simulator. Please try again or contact technical support."
      );
    }
  }

  function handleMenuClick(key) {
    const button = computedButtons[key];
    if (!button?.authorized) return;

    if (key === "simulate") {
      handleLaunchSimulator();
      return;
    }

    if (key === "release") {
      openPage("release");
      return;
    }

    openPage(key);
  }

  function renderActivePage() {
    switch (activePage) {
      case "uncheckedParts":
        return (
          <UncheckedParts
            onBack={goBackToMenu}
            onOpenConfirmPart={(partData) => openPage("confirmPart", partData)}
          />
        );

      case "allParts":
        return (
          <AllParts
            onBack={goBackToMenu}
            onOpenEditPart={(partData) => openPage("editPart", partData)}
          />
        );

      case "gages":
        return <Gages onBack={goBackToMenu} />;

      case "print":
        return <Print onBack={goBackToMenu} />;

      case "pir":
        return <PIR onBack={goBackToMenu} />;

      case "release":
        return (
          <Unreleased
            onBack={goBackToMenu}
            onOpenReleasePart={(partData) => openPage("releasePart", partData)}
          />
        );

      case "job":
        return <FrmJob onBack={goBackToMenu} />;

      case "importNj":
        return (
          <ImportFromNJ
            onBack={goBackToMenu}
            onOpenEditPart={(partData) => openPage("editPart", partData)}
          />
        );

      case "manual":
        return (
          <NewPart
            onBack={goBackToMenu}
            onOpenEditPart={(partData) => openPage("editPart", partData)}
          />
        );

      case "confirmPart":
        return (
          <ConfirmPart
            partNum={pageProps.partNum}
            rev={pageProps.rev}
            revMinor={pageProps.revMinor}
            onBack={() => openPage("uncheckedParts")}
            onConfirmed={() => openPage("uncheckedParts")}
          />
        );

      case "releasePart":
        return (
          <ReleasePart
            partNum={pageProps.partNum}
            rev={pageProps.rev}
            revMinor={pageProps.revMinor}
            onBack={() => openPage("release")}
            onReleased={() => openPage("release")}
          />
        );

      case "editPart":
        return (
          <EditPart
            partNum={pageProps.partNum}
            rev={pageProps.rev}
            revMinor={pageProps.revMinor}
            isImported={pageProps.isImported}
            isNew={pageProps.isNew}
            onBack={goBackToMenu}
            onOpenFrmSelect={(data) => openPage("frmSelect", data)}
            onDone={goBackToMenu}
          />
        );

      case "frmSelect":
        return (
          <FrmSelect
            partnum={pageProps.partnum}
            revMajor={pageProps.revMajor}
            revMinor={pageProps.revMinor}
            createdBy={pageProps.createdBy}
            onBack={goBackToMenu}
            onFinish={goBackToMenu}
          />
        );

      default:
        return null;
    }
  }

  function renderTile(button) {
    return (
      <div key={button.key} className="mafia-menu-grid-item">
        <button
          type="button"
          onClick={() => handleMenuClick(button.key)}
          disabled={!button.authorized}
          className={`mafia-menu-tile mafia-menu-tile-${button.tone} ${
            button.authorized ? "" : "mafia-menu-tile-disabled"
          }`}
        >
          <div className="mafia-menu-tile-icon">
            <i className={button.icon} />
          </div>

          <div className="mafia-menu-tile-content">
            <div className="mafia-menu-tile-title">{button.label}</div>
            <div className="mafia-menu-tile-subtitle">{button.subtitle}</div>
          </div>

          <div className="mafia-menu-tile-arrow">
            <i className="ri-arrow-right-line" />
          </div>
        </button>
      </div>
    );
  }

  function renderMenu() {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="MAFIA Aux" pageTitle="Applications" />

          <Row>
            <Col xxl={12}>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-3 p-lg-4">
                  <div className="mafia-menu-topbar">
                    <div>
                      <h4 className="mb-1 mafia-menu-heading">MAFIA Aux</h4>
                      <p className="text-muted mb-0 mafia-menu-subheading">
                        Select a module to continue.
                      </p>
                    </div>

                    <div className="mafia-menu-meta">
                      <Badge color="light" className="mafia-soft-badge text-uppercase">
                        {environmentText}
                      </Badge>
                      <Badge color="light" className="mafia-soft-badge text-uppercase">
                        Permission Level {permissionLevel}
                      </Badge>
                      <Badge color="light" className="mafia-soft-badge text-uppercase">
                        {accessibleCount} Actions
                      </Badge>
                    </div>
                  </div>

                  <div className="mafia-menu-grid-wrap">
                    {visibleButtons.map(renderTile)}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <style>
            {`
              .mafia-menu-topbar {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 1rem;
                flex-wrap: wrap;
                margin-bottom: 1rem;
              }

              .mafia-menu-heading {
                color: #0f172a;
                font-weight: 700;
              }

              .mafia-menu-subheading {
                font-size: 0.94rem;
              }

              .mafia-menu-meta {
                display: flex;
                align-items: center;
                gap: 0.55rem;
                flex-wrap: wrap;
              }

              .mafia-soft-badge {
                border: 1px solid rgba(15, 23, 42, 0.08);
                padding: 0.5rem 0.8rem;
                font-size: 0.72rem;
                letter-spacing: 0.06em;
                border-radius: 999px;
              }

              .mafia-menu-grid-wrap {
                display: grid;
                grid-template-columns: repeat(3, minmax(0, 1fr));
                gap: 0.9rem;
              }

              .mafia-menu-grid-item {
                min-width: 0;
              }

              .mafia-menu-grid-item:last-child:nth-child(3n + 1) {
                grid-column: 2 / 3;
              }

              .mafia-menu-tile {
                width: 100%;
                min-height: 118px;
                border: 1px solid rgba(15, 23, 42, 0.08);
                border-radius: 1rem;
                background: #ffffff;
                padding: 0.95rem 1rem;
                display: flex;
                align-items: flex-start;
                gap: 0.85rem;
                text-align: left;
                transition: all 0.2s ease;
                box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
              }

              .mafia-menu-tile:hover:not(.mafia-menu-tile-disabled) {
                transform: translateY(-2px);
                box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
                border-color: rgba(13, 110, 253, 0.28);
              }

              .mafia-menu-tile-disabled {
                opacity: 0.55;
                cursor: not-allowed;
                background: #f8fafc;
                box-shadow: none;
              }

              .mafia-menu-tile-icon {
                width: 42px;
                height: 42px;
                border-radius: 0.85rem;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
                font-size: 1rem;
              }

              .mafia-menu-tile-primary .mafia-menu-tile-icon {
                background: rgba(13, 110, 253, 0.12);
                color: #0d6efd;
              }

              .mafia-menu-tile-info .mafia-menu-tile-icon {
                background: rgba(13, 202, 240, 0.15);
                color: #087990;
              }

              .mafia-menu-tile-success .mafia-menu-tile-icon {
                background: rgba(25, 135, 84, 0.14);
                color: #198754;
              }

              .mafia-menu-tile-warning .mafia-menu-tile-icon {
                background: rgba(255, 193, 7, 0.18);
                color: #997404;
              }

              .mafia-menu-tile-danger .mafia-menu-tile-icon {
                background: rgba(220, 53, 69, 0.12);
                color: #dc3545;
              }

              .mafia-menu-tile-dark .mafia-menu-tile-icon,
              .mafia-menu-tile-secondary .mafia-menu-tile-icon {
                background: rgba(15, 23, 42, 0.08);
                color: #0f172a;
              }

              .mafia-menu-tile-content {
                flex: 1;
                min-width: 0;
              }

              .mafia-menu-tile-title {
                color: #0f172a;
                font-size: 0.98rem;
                font-weight: 700;
                margin-bottom: 0.2rem;
              }

              .mafia-menu-tile-subtitle {
                color: #64748b;
                font-size: 0.86rem;
                line-height: 1.4;
              }

              .mafia-menu-tile-arrow {
                color: #94a3b8;
                font-size: 1rem;
                padding-top: 0.1rem;
              }

              @media (max-width: 1199.98px) {
                .mafia-menu-grid-wrap {
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                }

                .mafia-menu-grid-item:last-child:nth-child(3n + 1) {
                  grid-column: auto;
                }
              }

              @media (max-width: 767.98px) {
                .mafia-menu-grid-wrap {
                  grid-template-columns: 1fr;
                }

                .mafia-menu-tile {
                  min-height: 108px;
                }
              }
            `}
          </style>
        </Container>
      </div>
    );
  }

  function renderChildPage() {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="MAFIA Aux" pageTitle="Applications" />
          <Row>
            <Col xxl={12}>
              <Card className="border-0 shadow-sm">
                <CardBody className="p-3 p-lg-4">{renderActivePage()}</CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return loading ? (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="MAFIA Aux" pageTitle="Applications" />
        <Row>
          <Col xxl={12}>
            <Card className="border-0 shadow-sm">
              <CardBody className="p-4 text-muted">Loading MAFIA Aux...</CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  ) : activePage !== "menu" ? (
    renderChildPage()
  ) : (
    renderMenu()
  );
}

export default function MenuPage() {
  return (
    <AppDialogProvider>
      <MenuPageContent />
    </AppDialogProvider>
  );
}

async function getCountryCode() {
  return "USA";
}

async function getMyPermissionLevel() {
  return 1;
}

async function launchSimulator(request) {
  console.log("Launch simulator for country:", request.countryCode);
  return {
    success: false,
    message:
      "Simulator launch is legacy external-EXE behavior. In the web version, this needs a backend or desktop launcher implementation.",
  };
}